import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";

const LOGO_URL = "/peter-logo-transparent.png";

const FALLBACK_NAVIGATION = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Practice Areas", path: "/services" },
  { label: "Contact", path: "/contact" },
];

const DEFAULT_CONTACT = {
  phone: "+31 687 519 025",
  emails: ["info@petvankort.com", "admin@petvankort.com"],
  address_lines: ["Floraweg 199", "1032 ZG Amsterdam", "The Netherlands"],
  office_region: "Amsterdam office serving clients with UK and EU legal reach.",
};

const DEFAULT_FOOTER_STATEMENT =
  "Let’s talk about the matter in front of you — and the outcome you want behind it.";

const navClassName = ({ isActive }) =>
  [
    "relative text-sm font-semibold uppercase tracking-[0.2em] transition-colors duration-300",
    isActive ? "text-slate-950" : "text-slate-500 hover:text-slate-950",
  ].join(" ");

export default function SiteLayout({ children, contact, firmName, footerStatement, navigation }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const safeFirmName = typeof firmName === "string" && firmName.trim() ? firmName : "Peters Van Kort";
  const safeFooterStatement =
    typeof footerStatement === "string" && footerStatement.trim()
      ? footerStatement
      : DEFAULT_FOOTER_STATEMENT;

  const safeNavigation = (Array.isArray(navigation) && navigation.length ? navigation : FALLBACK_NAVIGATION)
    .map((item, index) => ({
      label:
        typeof item?.label === "string" && item.label.trim()
          ? item.label.trim()
          : FALLBACK_NAVIGATION[index]?.label || `Link ${index + 1}`,
      path:
        typeof item?.path === "string" && item.path.trim()
          ? item.path.trim()
          : FALLBACK_NAVIGATION[index]?.path || "/",
    }))
    .filter((item) => item.label && item.path);

  const safeContact = {
    phone:
      typeof contact?.phone === "string" && contact.phone.trim()
        ? contact.phone
        : DEFAULT_CONTACT.phone,
    emails:
      Array.isArray(contact?.emails) && contact.emails.length
        ? contact.emails.filter((email) => typeof email === "string" && email.trim())
        : DEFAULT_CONTACT.emails,
    address_lines:
      Array.isArray(contact?.address_lines) && contact.address_lines.length
        ? contact.address_lines.filter((line) => typeof line === "string" && line.trim())
        : DEFAULT_CONTACT.address_lines,
    office_region:
      typeof contact?.office_region === "string" && contact.office_region.trim()
        ? contact.office_region
        : DEFAULT_CONTACT.office_region,
  };

  const phoneHref = `tel:${safeContact.phone.replace(/\s+/g, "")}`;

  return (
    <div className="min-h-screen bg-transparent text-slate-950" data-testid="site-layout-shell">
      <header className="sticky top-0 z-50 border-b border-slate-900/10 bg-white/80 backdrop-blur-xl" data-testid="site-header">
        <div className="page-shell flex items-center justify-between py-2">
          <Link className="flex items-center gap-4 md:gap-5" data-testid="site-logo-link" to="/">
            <span className="sr-only">{safeFirmName}</span>
            <div className="rounded-sm border border-amber-200/60 bg-gradient-to-br from-amber-50 via-white to-white p-2 shadow-[10px_10px_0_rgba(180,83,9,0.08)]" data-testid="site-logo-frame">
              <img
                alt={`${safeFirmName} logo`}
                className="h-24 w-auto object-contain brightness-[0.72] contrast-125 saturate-150 md:h-28"
                data-testid="site-logo-image"
                src={LOGO_URL}
              />
            </div>

            <div className="hidden md:block" data-testid="site-logo-slogan-block">
              <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-amber-700" data-testid="site-logo-slogan">
                Cross-Border Legal Counsel
              </p>
              <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-slate-500" data-testid="site-logo-service-line">
                Immigration • Litigation • UK & EU
              </p>
            </div>
          </Link>

          <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-navigation">
            {safeNavigation.map((item) => (
              <NavLink className={navClassName} data-testid={`desktop-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`} key={item.path} to={item.path}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              className="hidden items-center gap-2 border border-slate-950 bg-slate-950 px-5 py-3 text-xs font-bold uppercase tracking-[0.24em] text-white transition-colors duration-300 hover:bg-slate-800 lg:inline-flex"
              data-testid="header-contact-cta"
              to="/contact"
            >
              Contact now
              <ArrowUpRight className="h-4 w-4" />
            </Link>

            <button
              aria-label="Toggle navigation"
              className="inline-flex items-center justify-center border border-slate-900/15 p-3 text-slate-950 transition-colors duration-300 hover:bg-slate-50 lg:hidden"
              data-testid="mobile-menu-toggle-button"
              onClick={() => setMobileOpen((current) => !current)}
              type="button"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {mobileOpen ? (
          <div className="border-t border-slate-900/10 bg-white px-6 py-5 lg:hidden" data-testid="mobile-navigation-panel">
            <div className="flex flex-col gap-4">
              {safeNavigation.map((item) => (
                <NavLink
                  className={navClassName}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  key={item.path}
                  onClick={() => setMobileOpen(false)}
                  to={item.path}
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                className="inline-flex items-center justify-center gap-2 border border-slate-950 bg-slate-950 px-5 py-3 text-xs font-bold uppercase tracking-[0.24em] text-white"
                data-testid="mobile-contact-cta"
                onClick={() => setMobileOpen(false)}
                to="/contact"
              >
                Contact now
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        ) : null}
      </header>

      <main data-testid="site-main-content">{children}</main>

      <footer className="bg-slate-950 text-slate-300" data-testid="site-footer">
        <div className="page-shell py-24 md:py-32">
          <p className="text-[16vw] font-black uppercase leading-none text-white opacity-90 md:text-[12vw]" data-testid="footer-primary-heading">
            Let&apos;s talk
          </p>

          <div className="mt-10 grid gap-10 border-t border-white/10 pt-10 md:grid-cols-[1.4fr_1fr_1fr]">
            <div className="space-y-4">
              <img
                alt={`${safeFirmName} footer logo`}
                className="h-32 w-auto object-contain brightness-110 md:h-40"
                data-testid="footer-logo-image"
                src={LOGO_URL}
              />
              <p className="eyebrow text-amber-400" data-testid="footer-eyebrow">
                Cross-Border Legal Counsel
              </p>
              <p className="max-w-xl text-base leading-relaxed text-slate-300 md:text-lg" data-testid="footer-statement">
                {safeFooterStatement}
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-white" data-testid="footer-contact-heading">
                Contact
              </p>
              <a className="block text-lg text-white transition-colors duration-300 hover:text-amber-400" data-testid="footer-phone-link" href={phoneHref}>
                {safeContact.phone}
              </a>
              {safeContact.emails.map((email) => (
                <a className="block text-sm text-slate-300 transition-colors duration-300 hover:text-amber-400" data-testid={`footer-email-${email.replace(/[@.]/g, "-")}`} href={`mailto:${email}`} key={email}>
                  {email}
                </a>
              ))}
            </div>

            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-white" data-testid="footer-location-heading">
                Amsterdam office
              </p>
              {safeContact.address_lines.map((line, index) => (
                <p className="text-sm text-slate-300" data-testid={`footer-address-line-${index + 1}`} key={line}>
                  {line}
                </p>
              ))}
              <p className="text-sm text-slate-400" data-testid="footer-region-note">
                {safeContact.office_region}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}